/* import __COLOCATED_TEMPLATE__ from './edit-index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { COMPANY } from 'sh-www/models/company';
import { showModal } from 'sh-www/utils/modal-controls';
import {
  handleFormErrorOrModal,
  clearErrors,
  showGenericErrorModal,
  handleErrorResponse
} from 'sh-www/utils/handles-errors';

const CLASS_NAME = 'SH-c-admin-company-edit-index';
const EMPTY_COMPANY_GROUP_LABEL = 'No Group';

/** @type {string[]} */
const FORM_INPUTS = [
  'name',
  'slug',
  'type',
  'groupId',
  'sharesRates',
  'industries',
  'industryIds',
  'generic-exception',
  'saml_metadata_url',
];

export default class EditIndex extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-commands') companyCommandService;
  @service('commands/company-group-commands') companyGroupsCommandService;
  @service api;
  @service session;
  @service shData;
  @service store;

  @tracked errors = {};

  /** @param {Array} */
  @tracked companyGroups = [];

  @tracked industriesOptions;

  /** @param {Boolean} */
  @tracked isSubmitting = false;

  /**
   * Edited properties
   */
  @tracked _slug;
  @tracked _description;
  @tracked _type;
  @tracked _groupId;
  @tracked _sharesRates;

  @tracked _industries;

  @tracked samlIdp;
  @tracked samlIdpMetadataUrl;
  @tracked originalSamlIdpMetadataUrl;

  get baseClassName() {
    return CLASS_NAME;
  }

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  resetFormFields() {
    this._slug = undefined;
    this._description = undefined;
    this._type = undefined;
    this._groupId = undefined;
    this._sharesRates = undefined;
    this._industries = undefined;
  }

  get name() {
    return this.args.company.name;
  }

  get slug() {
    return this._slug || this.args.company.slug;
  }

  get description() {
    return this._description || this.args.company.description;
  }

  get type() {
    return this._type || this.args.company.type;
  }

  get groupId() {
    return this._groupId || this.args.company.groupId;
  }

  get sharesRates() {
    if (this._sharesRates !== undefined) {
      return this._sharesRates;
    }

    return this.args.company.sharesRates;
  }

  /** @type {Array} */
  get groupOptions() {
    return [
      {
        key: EMPTY_COMPANY_GROUP_LABEL,
        model: { id: false, label: EMPTY_COMPANY_GROUP_LABEL },
      },
      ...this.companyGroups.map((group) => ({
        key: group.name,
        model: group,
      }))
    ];
  }

  get typeOptions() {
    return Object.keys(COMPANY.TYPES).map((key) => ({
      key: COMPANY.TYPE_NAMES[COMPANY.TYPES[key]],
      model: { id: COMPANY.TYPES[key], label: COMPANY.TYPE_NAMES[COMPANY.TYPES[key]] },
    }));
  }

  get industryModels() {
    const industries = this._industries || this.args.company.industries.toArray() || [];
    return industries.sort((industryA, industryB) => industryA.name > industryB.name);
  }

  /** @type {String} */
  get groupName() {
    if (this._groupId === false || !this.groupId) {
      return EMPTY_COMPANY_GROUP_LABEL;
    }

    const companyGroup = this.companyGroups.find((companyGroup) => {
      return companyGroup.id === this.groupId;
    })

    return companyGroup?.name || EMPTY_COMPANY_GROUP_LABEL;
  }

  /** @type {String} */
  get typeName() {
    return COMPANY.TYPE_NAMES[this.type] || '';
  }

  get disableForm() {
    return this.submitTask.isRunning
      || this.args.reloadCompanyTask.isRunning
      || this.loadCompanySamlIdpsTask.isRunning;
  }

  get samlMetadataUrlHasChanges() {
    return this.originalSamlIdpMetadataUrl != this.samlIdpMetadataUrl;
  }

  @dropTask *loadCompanyGroupsTask() {
    let response;
    try {
      response = yield this.companyGroupsCommandService.loadAllCompanyGroups();
    } catch (error) {
      showGenericErrorModal(this, error);
      return;
    }

    this.shData.pushResponseToStore(response);

    this.companyGroups = this.shData.getModelArrayFromResponse(response, 'companyGroup').toArray();
  }

  @dropTask *loadIndustriesTask() {
    this.industriesOptions = yield this.companyCommandService.loadIndustries();
  }

  @dropTask
  *loadCompanySamlIdpsTask() {
    const response = yield this.companyCommandService.getSamlIdp(this.args.company);

    this.shData.pushResponseToStore(response);

    this.samlIdp = this.store.peekRecord('samlIdp', response.data.id);

    this.samlIdpMetadataUrl = this.samlIdp.metadataUrl;
    this.originalSamlIdpMetadataUrl = this.samlIdpMetadataUrl;
  }

  @dropTask *setGroupTask() {
    yield this.api.post(
      `/companies/${this.args.company.id}/company-groups/${this.groupId}`,
      {
        jsonData: {
          'company_group_shares_rates': this.sharesRates ? 1 : 0,
        },
      }
    );
  }

  @dropTask *removeGroupTask() {
    yield this.api.delete(
      `/companies/${this.args.company.id}/company-groups/${this.args.company.groupId}`,
      {}
    );
  }

  @dropTask *updateCompanyTask(data) {
    yield this.api.put(
      `/companies/${this.args.company.id}`,
      { jsonData: data }
    );
  }

  @dropTask *submitTask() {
    const industryIds = this.industryModels.map(industry => industry.id);
    clearErrors(this);

    const companyData = {
      'name': this.name,
      'type': this.type,
      'industries': industryIds,
      'slug': this.slug,
    };

    try {
      // Update group if needed
      if (this._groupId === false) {
        // Remove group
        yield this.removeGroupTask.perform();
      } else if (
        this._groupId
        && (this._groupId !== this.args.company.groupId
          || this._sharesRates !== this.args.company.sharesRates
        )
      ) {
        // Add/change group
        yield this.setGroupTask.perform();
      }

      // Update company
      yield this.updateCompanyTask.perform(companyData);
    } catch (error) {
      handleFormErrorOrModal(
        this,
        error,
        FORM_INPUTS,
        true,
        'Failed to create company'
      );

      return;
    }

    yield this.args.reloadCompanyTask.perform();

    this.applicationStateManager.addToast('Saved');
    this.resetFormFields();
  }

  /**
   * @param {String} field The name of the property.
   * @param {String} value The value of the property.
   */
  @action handleInput(field, value) {
    this[`_${field}`] = value;
  }

  /**
   * @param {String} field The name of the property.
   * @param {String} model The model of the selection.
   */
  @action handleSelect(field, model) {
    this[`_${field}`] = model.id;
  }

  /**
   * @param {String} model The model of the selection.
   */
  @action handleSelectGroup(newGroup) {
    if (!this.args.company.groupId) {
      this._sharesRates = true;
    }

    this._groupId = newGroup?.id || false;
  }

  /**
   * @param {String} field The name of the property.
   */
  @action toggleSharesRates() {
    this._sharesRates = !this.sharesRates;
  }

  @action onIndustryAdded(addedIndustry) {
    if (!this._industries) {
      this._industries = [...this.args.company.industries.toArray()];
    }

    // Check if already exists
    const industryIndex = this._industries.findIndex(industry => industry.id == addedIndustry.id);

    if (industryIndex === -1) {
      this._industries = [addedIndustry, ...this._industries];
      // this.set('_industries', [addedIndustry, ...this._industries]);
    }
  }

  @action onIndustryRemoved(removedIndustry) {
    if (!this._industries) {
      this._industries = [...this.args.company.industries.toArray()];
    }

    // Check if already exists
    const industryIndex = this._industries.findIndex(industry => industry.id == removedIndustry.id);

    if (industryIndex !== -1) {
      this._industries.splice(industryIndex, 1)
      this._industries = this._industries;
      // this.set('_industries', this._industries);
    }
  }

  @action onAddDomainClicked() {
    const { company } = this.args;
    showModal(this, 'company/add-domain', {
      company,
      onSubmit: this.args.reloadCompanyTask,
    });
  }

  @action
  onSamlMetadataUrlUpdate(text) {
    this.samlIdpMetadataUrl = text;

    if (this.errors?.saml_metadata_url) {
      this.errors = { ...this.errors, saml_metadata_url: undefined };
    }
  }

  @action
  onSubmitSamlClicked() {
    this.companyCommandService.setSamlIdpMetadataUrl(
      this.args.company,
      this.samlIdpMetadataUrl
    ).then(() => {
      return this.loadCompanySamlIdpsTask.perform();
    }).catch((e) => {
      handleErrorResponse(this, e, FORM_INPUTS);
      return;
    });
  }
}
